import { useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RecordOf } from 'immutable';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

import { selectGeofenceRules } from 'services/Geofences/selectors';

import { GeofenceRule } from 'services/resources';

import Ellipsing from 'components/Ellipsing/Ellipsing.component';
import GeofenceToggle from './GeofenceToggle.component';
import GeofencingModal from '../geofencingModal.component';

import { getIntlName } from './utils';

const useGeofencesTable = () => {
  const intl = useIntl();

  const geofences = useSelector(selectGeofenceRules());
  const [sorting, setSorting] = useState([]);

  const geofenceData = useMemo(() => {
    return geofences.toJSON();
  }, [geofences]);

  const columnHelper = createColumnHelper<RecordOf<GeofenceRule>>();

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor('isActive', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Geofence.isActive',
        }),
        cell: (cell) => {
          return <GeofenceToggle geofenceRule={cell.row.original} />;
        },
        size: 70,
        enableSorting: false,
      }),
      columnHelper.accessor('geofence', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Geofence.name',
        }),
        cell: (cell) => {
          return <Ellipsing text={getIntlName(cell.row.original, intl)} />;
        },
        size: 340,
        sortingFn: (rowA, rowB) => {
          const nameA = getIntlName(rowA.original, intl);
          const nameB = getIntlName(rowB.original, intl);
          return nameA.localeCompare(nameB);
        },
      }),
      columnHelper.display({
        header: intl.formatMessage({
          id: 'views.FleetSettings.Geofence.trigger',
        }),
        cell: (cell) => {
          const output = [];
          if (cell.row.original.onEntry) {
            output.push(
              intl.formatMessage({
                id: 'views.FleetSettings.Geofence.onEntry',
              })
            );
          }

          if (cell.row.original.onExit) {
            output.push(
              intl.formatMessage({
                id: 'views.FleetSettings.Geofence.onExit',
              })
            );
          }

          return output.join(', ');
        },
        size: 120,
      }),
      columnHelper.accessor('actions', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Geofence.actions',
        }),
        cell: (cell) => {
          const actionsList = cell.cell
            .getValue()
            .map((action) =>
              intl.formatMessage({
                id: `views.FleetSettings.Geofence.${action.type}`,
              })
            )
            .join(', ');
          return <Ellipsing text={actionsList} />;
        },
        size: 310,
      }),
      columnHelper.display({
        id: 'button-actions',
        cell: (cell) => {
          return <GeofencingModal.GeofenceButton values={cell.row.original.toJSON()} edit />;
        },
        size: 60,
      }),
    ];

    return columns;
  }, [columnHelper, intl]);

  const table = useReactTable({
    data: geofenceData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return { table };
};

export default useGeofencesTable;
